import { call, delay, put, takeLatest } from 'redux-saga/effects';

import axios from '../axiosConfig';
import {
  bookTripFailure,
  bookTripSuccess,
  changeTrip,
  getTripSuccess,
  pollBookingRequest,
} from '../../slices/trip';
import { SearchStatus } from '../../../constants';

function* pollBookingStatusWorker({
  payload: { tripId, form = { firstName: '', lastName: '', contactEmail: '' } },
}) {
  const errorMessage =
    "A network error occurred! Please check your email to see if your booking was successful. If you didn't receive a confirmation, click the book button again to retry, or reach out to TravelWits support for help.";
  try {
    const delayTime = 5000; // 5 seconds
    const maxRetries = 18; // 1.5 minute total

    let retries = 0;
    // Polling for booking status every 5 seconds
    while (true) {
      if (retries >= maxRetries) {
        yield put(bookTripFailure({ error: errorMessage }));
        break;
      }
      retries += 1;

      const dataPayload = {
        tripId,
        ...(form || {}),
      };
      const config = {
        method: 'post',
        url: 'trips/getBookingStatus',
        data: JSON.stringify(dataPayload),
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const { data } = yield call(axios, config);
      if (data?.status === SearchStatus.DONE) {
        yield put(bookTripSuccess({ data: data?.confirmation }));
        break;
      }
      if (data?.status === SearchStatus.SEARCHING) {
        yield put(changeTrip({ field: 'tripBookingStatus', value: data?.status }));
      }
      yield delay(delayTime);
    }
  } catch (error) {
    const errorMsg = error?.code === 'ERR_NETWORK' ? errorMessage : error.response?.data?.detail;

    if (error && error.response) {
      // NOTE:
      // Following code is duplicated from sagas\trip\tripBook.js saga.
      // If making changes here, please make sure to update sagas\trip\tripBook.js as well.
      // TODO: Refactor to avoid duplication.
      if (error.response.status === 406) {
        if (error.response.data.clientOverwriteWarning) {
          yield put(
            bookTripFailure({
              clientOverwriteWarning: error.response.data.clientOverwriteWarning,
            }),
          );
          return;
        }
        if (error.response.data.trip) {
          const { data } = error.response;
          yield put(
            getTripSuccess({
              tripId,
              trip: {
                ...(data.trip || {}),
                maxCharsNoteToAirDesk: data.maxCharsNoteToAirDesk,
                doShowNoteToAirDeskInput: data.doShowNoteToAirDeskInput,
                doShowCompanyNameInput: data.doShowCompanyNameInput,
                doShowAgencyPlanningFeesPerPassenger: data.doShowAgencyPlanningFeesPerPassenger,
                separatePnrForTravelTypes: data.separatePnrForTravelTypes,
                tripBookingFees: data.tripBookingFees,
              },
            }),
          );
        }
        let errorText = 'Your trip availability/price is changed!';
        if (error.response.data.isFareUnavailable) {
          errorText =
            'Fare is not available. Contact airdesk for issues related to flight booking.';
        } else if (error.response.data.isPriceQuoteChanged) {
          errorText =
            'Flight price has changed. Contact airdesk for issues related to flight booking.';
        }
        // Trip updated exception (from api it will return NotAcceptable response status)
        yield put(
          bookTripFailure({
            error: errorText,
            isTripAvailabilityPriceChanged: true,
            tripAvailabilityPriceChangeResponse: error.response.data,
          }),
        );
        return;
      }
    }
    yield put(bookTripFailure({ error: errorMsg }));
  }
}

function* pollBookingStatusWatcher() {
  yield takeLatest(pollBookingRequest.toString(), pollBookingStatusWorker);
}

export default pollBookingStatusWatcher;
